import React, { useEffect, useState } from "react";

import { getRechargeHistory } from "../../services/walletService";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";

const RechargeHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getRechargeHistory(page, 10);
      setOrders(data.result.rechargeHistory);
      setTotalPages(data.result.pagination.totalPagesCount);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [page]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "success":
        return {
          bg: "bg-green-500/10",
          text: "text-green-400",
          icon: CheckCircle2,
        };
      case "pending":
        return {
          bg: "bg-yellow-500/10",
          text: "text-yellow-400",
          icon: Clock,
        };
      default:
        return {
          bg: "bg-red-500/10",
          text: "text-red-400",
          icon: XCircle,
        };
    }
  };

  return (
    <div>
      <h3 className="text-white font-bold text-lg mb-6">Recharge History</h3>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <CreditCard size={40} className="mb-3 opacity-30" />
          <p className="text-sm">No recharge history found.</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {orders.map((order) => {
              const statusStyle = getStatusStyle(order.status);
              const StatusIcon = statusStyle.icon;
              return (
                <div
                  key={order._id}
                  className="bg-[#2A1B3D]/50 border border-white/5 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <CreditCard size={14} className="text-purple-400" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-bold">
                          ₹{order.amount}
                        </span>
                        <span className="text-gray-500 text-xs ml-2">
                          {order.coins} coins
                        </span>
                      </div>
                    </div>
                    <span
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}
                    >
                      <StatusIcon size={12} />
                      {order.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-xs">
                      {format(
                        new Date(order.createdOn),
                        "dd MMM yyyy, hh:mm a"
                      )}
                    </span>
                    <span className="text-gray-500 text-xs capitalize">
                      {order.gateway}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Order ID
                  </th>
                  <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Amount (INR)
                  </th>
                  <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Coins
                  </th>
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Gateway
                  </th>
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const statusStyle = getStatusStyle(order.status);
                  const StatusIcon = statusStyle.icon;
                  return (
                    <tr
                      key={order._id}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {format(
                          new Date(order.createdOn),
                          "dd MMM yyyy, hh:mm a"
                        )}
                      </td>
                      <td
                        className="py-3 px-4 text-gray-500 text-xs max-w-[120px] truncate"
                        title={order.orderId}
                      >
                        {order.orderId}
                      </td>
                      <td className="py-3 px-4 text-right text-white text-sm font-medium">
                        ₹{order.amount}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300 text-sm">
                        {order.coins}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm capitalize">
                        {order.gateway}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}
                        >
                          <StatusIcon size={12} />
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-gray-400 text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default RechargeHistory;
